import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CustomButton } from 'shared/ui/custom-button';
import { Checkbox } from 'shared/ui/checkbox';
import { ArrowDownIcon } from 'shared/ui/icons';
import Input from 'shared/ui/input';
import { MENU_ID } from 'shared/constants/menu';
import { menuGroupList } from 'shared/constants/menu-catalog';
import { useRoleForm } from '../../hooks/useRoleForm';

// Map lookup replaces the prior nested-ternary label expression.
// Order: submitting > edit-mode > create-mode.
const SUBMIT_LABEL = {
  submitting: 'Loading...',
  edit: 'save',
  create: 'create_role',
};

const resolveSubmitLabel = ({ isSubmiting, isEdit, t }) => {
  if (isSubmiting) return SUBMIT_LABEL.submitting;
  return t(isEdit ? SUBMIT_LABEL.edit : SUBMIT_LABEL.create);
};

const MenuCheckboxGroup = ({ val, formData, isSubmited, handleChecked, t }) => {
  const isChecked = formData.find((menu) => menu.menu_id === val.menu_id);
  const isSpecialMenu =
    val.menu_id === MENU_ID.KLAIM_TPA ||
    val.menu_id === MENU_ID.ANALYTICS_DASHBOARD;

  // Menus exposing the full claim permission set (View/Create/Edit/Delete +
  // Approval + All Claim). Insurance Portal and Quality Checker share it.
  const hasClaimPermissions =
    val.menu_id === MENU_ID.INSURANCE_PORTAL ||
    val.menu_id === MENU_ID.INSURANCE_CLAIM_QC;

  const isAllChecked = isSpecialMenu
    ? val.supportedPermissions?.every((permission) => isChecked?.[permission])
    : isChecked?.can_read &&
      isChecked?.can_create &&
      isChecked?.can_update &&
      isChecked?.can_delete;

  return (
    <div className="bg-neutral-50 p-4 mb-2 rounded-2xl">
      <div className="flex">
        <Checkbox
          disabled={isSubmited}
          checked={isAllChecked}
          onClick={({ target }) =>
            !isSubmited && handleChecked(target.checked, val, 'all')
          }
          label={
            <div className="flex items-center gap-2">
              {val.icon}
              <p>{val.nameKey ? t(val.nameKey) : val.name}</p>
            </div>
          }
          id={val.menu_id}
        />
      </div>
      <div className="ml-6 mt-3 flex gap-6">
        <div className="flex gap-1 items-center text-xs">
          <Checkbox
            defaultChecked={val.can_read}
            checked={isChecked?.can_read}
            disabled={isSubmited}
            onClick={({ target }) =>
              !isSubmited && handleChecked(target.checked, val, 'can_read')
            }
            label="View"
            id={`${val.menu_id}_can_read`}
          />
        </div>

        {!isSpecialMenu && (
          <>
            <div className="flex gap-1 items-center text-xs">
              <Checkbox
                defaultChecked={val.can_create}
                checked={isChecked?.can_create}
                disabled={isSubmited}
                onClick={({ target }) =>
                  !isSubmited &&
                  handleChecked(target.checked, val, 'can_create')
                }
                label="Create"
                id={`${val.menu_id}_can_create`}
              />
            </div>
            <div className="flex gap-1 items-center text-xs">
              <Checkbox
                defaultChecked={val.can_update}
                checked={isChecked?.can_update}
                disabled={isSubmited}
                onClick={({ target }) =>
                  !isSubmited &&
                  handleChecked(target.checked, val, 'can_update')
                }
                label="Edit"
                id={`${val.menu_id}_can_update`}
              />
            </div>
            <div className="flex gap-1 items-center text-xs">
              <Checkbox
                defaultChecked={val.can_delete}
                checked={isChecked?.can_delete}
                disabled={isSubmited}
                onClick={({ target }) =>
                  !isSubmited &&
                  handleChecked(target.checked, val, 'can_delete')
                }
                label="Delete"
                id={`${val.menu_id}_can_delete`}
              />
            </div>
          </>
        )}

        {hasClaimPermissions && (
          <>
            <div className="flex gap-1 items-center text-xs">
              <Checkbox
                defaultChecked={val.can_approve}
                checked={isChecked?.can_approve}
                disabled={isSubmited}
                onClick={({ target }) =>
                  !isSubmited &&
                  handleChecked(target.checked, val, 'can_approve')
                }
                label="Approval"
                id={`${val.menu_id}_can_approve`}
              />
            </div>
            <div className="flex gap-1 items-center text-xs">
              <Checkbox
                defaultChecked={val.can_read_all}
                checked={isChecked?.can_read_all}
                disabled={isSubmited}
                onClick={({ target }) =>
                  !isSubmited &&
                  handleChecked(target.checked, val, 'can_read_all')
                }
                label="All Claim"
                id={`${val.menu_id}_can_read_all`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const RoleFormPanel = ({ id }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const {
    availableMenus,
    menus,
    roleName,
    formData,
    errors,
    isLoadingDetail,
    isLoadingMenus,
    isSubmited,
    hideMenus,
    hideMenuChild,
    setRoleName,
    setErrors,
    handleChecked,
    handleCheckedParent,
    toggleMenu,
    onSubmit,
  } = useRoleForm({ id });

  const CheckboxGroup = useCallback(
    ({ val }) => (
      <MenuCheckboxGroup
        val={val}
        formData={formData}
        isSubmited={isSubmited}
        handleChecked={handleChecked}
        t={t}
      />
    ),
    [formData, isSubmited, handleChecked, t]
  );

  const isFormLoading = isLoadingDetail || isLoadingMenus;
  const isEdit = Boolean(id);
  const submitLabel = resolveSubmitLabel({
    isSubmiting: isSubmited,
    isEdit,
    t,
  });

  return (
    <div className="w-full bg-white border rounded-lg flex flex-wrap p-6">
      <div className="w-full">
        <div className="w-full flex flex-wrap gap-y-2">
          <label className="text-sm font-normal">{t('role_name')}</label>
          <Input
            className="w-full"
            placeholder={t('role_name')}
            onChange={(e) => {
              setRoleName(e.target.value);
              setErrors();
            }}
            value={roleName}
            aria-label={t('role_name')}
          />
          {errors?.role && (
            <div className="w-full flex">
              <span className="text-red-500 text-sm">{errors.role}</span>
            </div>
          )}
        </div>

        <div className="pt-4 pb-4 text-neutral-900 text-sm font-medium flex justify-between">
          {t('access_menu')}
        </div>

        <div className="border border-neutral-200 rounded-2xl p-4">
          {isFormLoading
            ? [1, 2, 3].map((_, i) => (
                <div key={i} className="animate-pulse border-b pb-4 pt-6">
                  <div className="h-2.5 bg-gray-200 rounded-full w-48 mb-4" />
                  <div className="ml-9">
                    <div className="h-2.5 bg-gray-200 rounded-full w-16 mb-4" />
                    <div className="flex gap-4">
                      <div className="h-2.5 bg-gray-200 rounded-full w-32 mb-4" />
                      <div className="h-2.5 bg-gray-200 rounded-full w-32 mb-4" />
                    </div>
                  </div>
                </div>
              ))
            : menuGroupList.map((group, index) => {
                const groupInApiMenus = menus.find(
                  (menu) => menu.id === group.api_id
                );
                if (!groupInApiMenus) return null;

                return (
                  <React.Fragment key={index}>
                    {group.cascade && (
                      <button
                        type="button"
                        className="w-full pt-4 pb-2 pl-3 text-neutral-900 text-sm font-normal flex justify-between cursor-pointer border-b-2 border-neutral-50 text-left"
                        onClick={() => toggleMenu(group.id)}
                        aria-expanded={!hideMenus.includes(group.id)}
                        aria-label={
                          group.nameKey ? t(group.nameKey) : group.name
                        }
                      >
                        {group.nameKey ? t(group.nameKey) : group.name}
                        <ArrowDownIcon
                          className={`transform transition-transform cursor-pointer ${
                            hideMenus.includes(group.id) ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                    )}

                    {availableMenus.map((val, i) => {
                      if (val.menu_group_id !== group.id) return null;
                      if (hideMenus.includes(group.id)) return null;

                      if (val.child_menu) {
                        return (
                          <div
                            key={i}
                            className="bg-neutral-50 p-4 mb-2 rounded-2xl"
                          >
                            <div className="text-neutral-900 text-sm font-normal flex justify-between">
                              <div className="flex gap-1 items-center">
                                <Checkbox
                                  disabled={isSubmited}
                                  onClick={({ target }) =>
                                    handleCheckedParent(target, val)
                                  }
                                  label={
                                    <div className="flex items-center gap-2">
                                      {val.icon}
                                      <p>{val.name}</p>
                                    </div>
                                  }
                                  id={val.menu_id}
                                />
                              </div>
                              <button
                                type="button"
                                className="cursor-pointer"
                                onClick={() => toggleMenu(val.menu_id, true)}
                                aria-expanded={
                                  !hideMenuChild.includes(val.menu_id)
                                }
                                aria-label={`Toggle ${val.name} sub-menus`}
                              >
                                <ArrowDownIcon
                                  className={`transform transition-transform ${
                                    hideMenuChild.includes(val.menu_id)
                                      ? 'rotate-180'
                                      : ''
                                  }`}
                                />
                              </button>
                            </div>

                            {!hideMenuChild.includes(val.menu_id) && (
                              <div className="ml-2 mt-4">
                                {val.child_menu.map((child, idx) => (
                                  <CheckboxGroup key={idx} val={child} />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }

                      return <CheckboxGroup key={i} val={val} />;
                    })}
                  </React.Fragment>
                );
              })}

          {errors?.menus && (
            <div className="w-full flex">
              <span className="text-red-500 text-sm">{errors.menus}</span>
            </div>
          )}
        </div>

        {!isFormLoading && (
          <div className="flex items-center w-full justify-end gap-2 mt-6">
            <CustomButton
              type="outline"
              size="sm"
              disabled={isSubmited}
              onClick={() => navigate('/management-role')}
            >
              {t('cancel')}
            </CustomButton>
            <CustomButton
              type="primary"
              size="sm"
              disabled={isSubmited}
              isLoading={isSubmited}
              onClick={onSubmit}
            >
              {submitLabel}
            </CustomButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleFormPanel;
